/* eslint-disable no-console, fp/no-loops, no-case-declarations */
import { flowRight, isEmpty, merge } from 'lodash';
import type { Decoration, Node } from 'ricos-schema';
import { Decoration_Type, Node_Type } from 'ricos-schema';

import type {
  RicosContentBlock,
  RicosEntityMap,
  RicosEntityRange,
  RicosInlineStyleRange,
} from '../../../types';
import {
  createTextNode,
  generateRangeMap,
  mergeOverlappingEntityRanges,
  removeOverlappingRangesWithSameStyle,
} from '../../nodeUtils';
import { TO_RICOS_DECORATION_TYPE, TO_RICOS_INLINE_STYLE_TYPE } from '../consts';
import type { FromDraftOptions } from './fromDraft';
import { getEntity } from './getRicosEntityData';

type KeyType = string | number;
type StyleType = string;

const HEX_COLOR_REGEXP = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

const isEmptyTextNode = node => node.type === Node_Type.TEXT && node.textData?.text === '';

const removeUnsupportedEntities = (
  entityRanges: RicosEntityRange[],
  entityMap: RicosEntityMap
): RicosEntityRange[] =>
  entityRanges.filter(range => TO_RICOS_DECORATION_TYPE[entityMap[range.key]?.type] !== undefined);

const removeUnsupportedDecorations = (
  inlineStyleRanges: RicosInlineStyleRange[]
): RicosInlineStyleRange[] => inlineStyleRanges.filter(range => isDecorationType(range.style));

const mergeColorDecorations = (decorations: Decoration[]): Decoration[] => {
  const colorDecorations = decorations.filter(
    decoration => decoration.type === Decoration_Type.COLOR
  );
  const otherDecorations = decorations.filter(
    decoration => decoration.type !== Decoration_Type.COLOR
  );
  return colorDecorations.length > 0
    ? otherDecorations.concat([merge({}, ...colorDecorations)])
    : otherDecorations;
};

const removeDuplicateDecorations = (decorations: Decoration[]): Decoration[] => {
  const seenDecorations = new Set();

  return [...decorations.reverse()]
    .filter(decoration => {
      if (seenDecorations.has(decoration.type)) {
        return false;
      }
      seenDecorations.add(decoration.type);
      return true;
    })
    .reverse();
};

const isDecorationType = (decorationType = '') =>
  TO_RICOS_DECORATION_TYPE[decorationType] !== undefined ||
  TO_RICOS_INLINE_STYLE_TYPE[decorationType] !== undefined ||
  Object.keys(dynamicDecorationGetters).some(
    dynamicType => decorationType.includes(dynamicType) && isValidDecorationColor(decorationType)
  ) ||
  HEX_COLOR_REGEXP.test(decorationType);

const getTextNodesWithLineBreaks = (textNodes: Node[]): Node[] =>
  textNodes
    .map(node =>
      extractLineBreaks(node.textData?.text ?? '').map(
        text =>
          ({
            ...createTextNode(text),
            textData: {
              text,
              decorations: text === '\n' ? [] : [...(node.textData?.decorations ?? [])],
            },
          } as Node)
      )
    )
    .reduce((nodes: Node[], nodesArr: Node[]) => [...nodes, ...nodesArr], [] as Node[]);

const extractLineBreaks = (text: string) => {
  if (!text.includes('\n') || text === '\n') {
    return [text];
  }
  const texts = text.split(/(\\*\n)/);
  const newLineRegex = /\\(\\)*\n$/;
  return (
    texts
      .filter(text => text.length !== 0)
      .map(text => {
        return newLineRegex.test(text) ? [text.replace('\n', ''), '\n'] : [text];
      })
      .reduce((acc, val) => [...acc, ...val], [])
      // needed to combine back text with escaped slashes.
      // we dont want \\ to create a seperate TEXT node. we combine it with
      // previous TEXT node, that isnt \n. For example:
      // ['some text', '\\', '\n'] => ['some text\\', '\n']
      .reduce((allTexts, current, index, array) => {
        if (index === 0) {
          return [current];
        }
        if (current === '\n') {
          return [...allTexts, current];
        }

        if (array[index - 1] === '\n') {
          return [...allTexts, current];
        }

        return [...allTexts.slice(0, -1), allTexts[allTexts.length - 1] + current];
      }, [])
  );
};

const dynamicDecorationGetters = {
  FG: (style: string) => {
    return {
      type: Decoration_Type.COLOR,
      colorData: { foreground: style },
    };
  },
  BG: (style: string) => {
    return {
      type: Decoration_Type.COLOR,
      colorData: { background: style },
    };
  },
  'font-size': (style: string) => {
    const [value, unit] = style.split(/(px|em)/gi);
    return {
      type: Decoration_Type.FONT_SIZE,
      fontSizeData: {
        unit: unit.toUpperCase(),
        value: parseInt(value),
      },
    };
  },
};

export const getTextNodes = (
  block: RicosContentBlock,
  entityMap: RicosEntityMap,
  { ignoreUnsupportedValues }: FromDraftOptions = {},
  withLineBreaks = true
): Node[] => {
  const createTextNodeWithDecorations = ({
    text,
    styles = [],
    keys = [],
  }: {
    text: string;
    styles: StyleType[];
    keys: KeyType[];
  }): Node => {
    const normalizedText = normalizeText(text);
    const textNode: Node = createTextNode(normalizedText);

    let decorations: Decoration[] = [];

    const keysDecorations = removeDuplicateDecorations(
      keys.map(key => getEntity(key, entityMap)).filter(x => x) as Decoration[]
    );

    const stylesDecorations = flowRight(
      removeDuplicateDecorations,
      mergeColorDecorations
    )(styles.map(style => getDecoration(style)));

    decorations = [...decorations, ...keysDecorations, ...stylesDecorations].sort(
      decorationComparator
    );

    if (!isEmpty(decorations) && textNode.textData) {
      textNode.textData.decorations = decorations;
    }

    return textNode;
  };

  const getDecoration = (style: StyleType): Decoration => {
    let decoration: Decoration;
    try {
      const isLegacyFontColorDecoration = HEX_COLOR_REGEXP.test(style);
      const styleObj = isLegacyFontColorDecoration ? { FG: style } : JSON.parse(style);
      const type = Object.keys(styleObj)[0];
      const value = Object.values<string>(styleObj)[0];
      decoration = dynamicDecorationGetters[type](value);
    } catch {
      if (!isDecorationType(style) && !ignoreUnsupportedValues) {
        throw Error(`Unknown decoration type "${style}"!`);
      }
      decoration = TO_RICOS_INLINE_STYLE_TYPE[style] || {
        type: TO_RICOS_DECORATION_TYPE[style],
      };
    }
    return decoration;
  };

  const { text = '', inlineStyleRanges, entityRanges } = block;
  if (text.length === 0) {
    return [];
  }

  const dedupedStyleRanges = removeOverlappingRangesWithSameStyle(inlineStyleRanges);

  const supportedInlineStyleRanges = ignoreUnsupportedValues
    ? removeUnsupportedDecorations(dedupedStyleRanges)
    : dedupedStyleRanges;

  const mergedEntityRanges = mergeOverlappingEntityRanges(entityRanges);

  const rangeMap = generateRangeMap([
    ...supportedInlineStyleRanges,
    ...removeUnsupportedEntities(mergedEntityRanges, entityMap),
  ]);

  const textNodes: Node[] = [];

  let styles: StyleType[] = [];
  let keys: KeyType[] = [];

  const numbers = Object.keys(rangeMap).map(Number);
  numbers.forEach((point, i) => {
    if (numbers[i] < text.length) {
      rangeMap[point].forEach(entry => {
        if (entry.action === 'start') {
          if ('style' in entry) {
            styles.push(entry.style);
          } else {
            keys.push(entry.key);
          }
        } else if ('style' in entry) {
          styles = styles.filter(s => s !== entry.style);
        } else {
          keys = keys.filter(s => s !== entry.key);
        }
      });

      textNodes.push(
        createTextNodeWithDecorations({
          text: Array.from(text)
            .slice(numbers[i], numbers[i + 1])
            .join(''),
          styles,
          keys,
        })
      );
    }
  });

  const nonEmptyTextNodes = textNodes.filter(node => !isEmptyTextNode(node));

  return withLineBreaks ? getTextNodesWithLineBreaks(nonEmptyTextNodes) : nonEmptyTextNodes;
};

const decorationComparator = (a: Decoration, b: Decoration) => a.type.localeCompare(b.type);

const isValidDecorationColor = (style: string) => {
  try {
    const styleObj = JSON.parse(style);
    const value = Object.values<string>(styleObj)[0];

    if (value.startsWith('#')) {
      return HEX_COLOR_REGEXP.test(value);
    }
    return true;
  } catch {
    return false;
  }
};

const normalizeText = (text: string): string => {
  return removeHeadingMarkers(removeUnsupportedUnicodeChars(text));
};

const removeUnsupportedUnicodeChars = (text: string): string => {
  return text.replace(/[\u2028\u2029]/g, '');
};

const removeHeadingMarkers = (text: string): string => {
  return text.replace(/^\{\$h([1-5])\}(.*?)\{\$h\}/g, matchedText =>
    matchedText.replace(/^\{\$h[1-5]\}|\{\$h\}$/g, '')
  );
};
